import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Finding, FindingsApi, Status111Enum } from 'api-client';
import { Timeline } from "@/components/Timeline";
import { AssetNotes } from '../../components/assets/detail/AssetNotes';

// Import necessary icons
import {
  Clock,
  Activity,
  Download,
  Trash,
  Link as LinkIcon,
  ExternalLink,
  Check,
  RefreshCw,
  X,
  Copy,
  Shield,
  Tag
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { getApiConfiguration } from '@/api/utils';

// Define a type adapter to convert API LogEntry to Timeline LogEntry
interface TimelineLogEntry {
  id: string;
  actorDetails: {
    full_name: string;
  };
  verboseDescription: string;
  timestamp: string;
}

// Helper function to convert API LogEntry to Timeline LogEntry
const adaptLogEntryForTimeline = (apiLogEntry: import('api-client').LogEntry): TimelineLogEntry => {
  // Parse actorDetails from string to object if needed
  let actorDetailsObj;
  try {
    actorDetailsObj = typeof apiLogEntry.actorDetails === 'string' 
      ? JSON.parse(apiLogEntry.actorDetails) 
      : apiLogEntry.actorDetails;
  } catch (e) {
    actorDetailsObj = { full_name: 'Unknown User' };
  }

  return {
    id: apiLogEntry.id.toString(), // Convert number to string
    actorDetails: {
      full_name: actorDetailsObj.full_name || 'Unknown User'
    },
    verboseDescription: apiLogEntry.verboseDescription,
    timestamp: apiLogEntry.timestamp ? new Date(apiLogEntry.timestamp).toISOString() : new Date().toISOString()
  };
};

const FindingDetail = () => {
  const { id } = useParams();
  const [finding, setFinding] = useState<Finding | null>(null);
  const [logEntries, setLogEntries] = useState<TimelineLogEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentTeam } = useTeams();
  const teamSlug = currentTeam?.slug;
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !teamSlug || !token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const findingsApi = new FindingsApi(getApiConfiguration(token));
        const [findingResponse] = await Promise.all([
          findingsApi.findingsRetrieve({
            id,
            teamSlug,
          }),
        ]);
        setFinding(findingResponse);
        // Convert API LogEntry array to Timeline LogEntry array
        setLogEntries(findingResponse.history ? findingResponse.history.map(adaptLogEntryForTimeline) : []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, teamSlug, token]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!finding) {
    return <div>Finding not found</div>;
  }
  console.log(finding);
  // Helper to return a fallback string if value is empty.
  const safeValue = (value: any) =>
    value === undefined || value === null || value === '' ? 'UnKnown' : value;


  const handleAction = (action: string) => {
    console.log(`Performing ${action} on finding ${id}`);
  };

  const UpdateFinding = async (updatedFinding: Finding, state: Status111Enum) => {
    if (!id || !teamSlug || !token) {
      return;
    }
    setError(null);

    try {
      const findingsApi = new FindingsApi(getApiConfiguration(token));
      const findingResponse = await findingsApi.findingsUpdate({
        id,
        teamSlug,
        finding: {
          title: updatedFinding?.title,
          severity: updatedFinding?.severity,
          status: state,
          classification: updatedFinding?.classification,
          stepsToReproduce: updatedFinding?.stepsToReproduce,
          remediationSteps: updatedFinding?.remediationSteps,
          references: updatedFinding?.references,
          assetName: updatedFinding?.assetName,
          overview: updatedFinding?.overview,
          impact: updatedFinding?.impact,
          details: updatedFinding?.details,
          endpoint: updatedFinding?.endpoint,
          tags: updatedFinding?.tags
        } as Omit<Finding, 'id'|'asset_type_name'|'asset_name'|'created_at'|'evidences'|'updated_at'|'history'>
      });
      
      setFinding(findingResponse);
      // Convert API LogEntry array to Timeline LogEntry array
      setLogEntries(findingResponse.history ? findingResponse.history.map(adaptLogEntryForTimeline) : []);
      return findingResponse;
    } catch (err) {
      console.error('Error updating finding:', err);
      setError(err instanceof Error ? err.message : 'Failed to update finding');
      throw err;
    } 
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-lg bg-red-500/10">
              <Shield className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">
                  {safeValue(finding.title)}
                </h1>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <Badge variant={safeValue(finding.severity)}>
                  {safeValue(finding.severity.toUpperCase())}
                </Badge>
                <Badge variant={safeValue(finding.status)}>
                  {safeValue(finding?.status?.toUpperCase().replace('_', ' '))}
                </Badge>

                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Tag className="h-4 w-4" />
                  <span
                    key={safeValue(finding.classification?.cwe)}
                    className="px-2 py-0.5 rounded-full bg-gray-800/50 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
                  >
                    {safeValue(finding.classification?.cwe)}
                  </span>
                  <div className="flex items-center space-x-2">
                    {(finding.tags || []).map((tag: any, index: number) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 rounded-full bg-gray-800/50 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
                      >
                        {safeValue(tag)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button disabled variant="outline" onClick={() => UpdateFinding(finding, Status111Enum.Resolved)}>
              <Check className="h-4 w-4 mr-2" />
              Mark as Resolved
            </Button>
            <Button disabled variant="outline" onClick={() => UpdateFinding(finding, Status111Enum.Open)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Request Retest
            </Button>

          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="space-y-1">
            <div className="text-sm text-gray-400">CVSS Score</div>
            <div className="text-lg font-medium text-red-500">
              {finding.classification &&
              finding.classification.cvss &&
              finding.classification.cvss.score !== undefined
                ? finding.classification.cvss.score
                : 'UnKnown'}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-gray-400">Discovered</div>
            <div className="text-sm text-white">
              {finding.createdAt
                ? new Date(finding.createdAt).toLocaleString()
                : 'UnKnown'}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-gray-400">Last Updated</div>
            <div className="text-sm text-white">
              {finding.updatedAt
                ? new Date(finding.updatedAt).toLocaleString()
                : 'UnKnown'}
            </div>
          </div>
          {/* 
            <div className="space-y-1">
            <div className="text-sm text-gray-400">Assignee</div>
            <div className="flex items-center space-x-2">
              {finding.assignee && finding.assignee.avatar ? (
                <img
                  src={finding.assignee.avatar}
                  alt={safeValue(finding.assignee.name)}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <span className="text-sm text-white">UnKnown</span>
              )}
              <span className="text-sm text-white">
                {safeValue(finding.assignee?.name)}
              </span>
            </div>
          </div>
          */}

        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">
              Description
            </h2>
            <p className="text-gray-300 whitespace-pre-wrap">
              {safeValue(finding.overview)}
            </p>
          </div>

          {/* Technical Details */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">
              Technical Details
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-400">
                    Affected Component
                  </div>
                  <div className="text-sm text-white mt-1">
                    {safeValue(finding.component)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">
                    Affected Endpoint
                  </div>
                  <div className="text-sm text-white mt-1">
                    {finding.endpoint && (
                      <a
                        href={finding.endpoint?.startsWith('http') ? finding.endpoint : `https://${finding.endpoint}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-blue-400 transition-colors duration-200 block max-w-md overflow-hidden text-ellipsis whitespace-nowrap"
                        title={finding.endpoint}
                      >
                        {finding.endpoint}
                      </a>
                    )}
                    {!finding.endpoint && '-'}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Details</div>
                <div className="text-sm text-white">
                  {finding.details
                    ? typeof finding.details === 'string'
                      ? finding.details
                      : JSON.stringify(finding.details)
                    : 'UnKnown'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">
                  Steps to Reproduce
                </div>
                <div
                  className="text-sm text-white"
                  dangerouslySetInnerHTML={{
                    __html: finding.stepsToReproduce
                      ? finding.stepsToReproduce.split('\n').join('<br />')
                      : 'UnKnown'
                  }}
                />
              </div>
              <div>
                <div className="text-sm text-gray-400">Impact</div>
                <div className="text-sm text-white">
                  {safeValue(finding.impact)}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-2">
                  Remediation
                </div>
                <p
                  className="text-sm text-white"
                  dangerouslySetInnerHTML={{
                    __html: finding.remediationSteps
                      ? finding.remediationSteps.split('\n').join('<br />')
                      : 'UnKnown'
                  }}
                />
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-2">
                  References
                </div>
                <div className="space-y-2">
                  {finding.references.map((link: any) => (
                    <a
                      key={link}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-sm text-blue-500 hover:text-blue-400"
                    >
                      <LinkIcon className="h-4 w-4" />
                      <span>{link}</span>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <AssetNotes objectId={finding.id?.toString()} objectType="finding" />;

          {/* Evidence 
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">
                Evidence
              </h2>
              <Button>
                <Upload className="h-4 w-4 mr-2" />
                Upload Evidence
              </Button>
            </div>

            <div className="space-y-4">
              {(finding.evidence || []).length > 0 ? (
                (finding.evidence || []).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg group"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-2 rounded-lg bg-blue-500/10">
                        <FileText className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">
                          {safeValue(item.title)}
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          {safeValue(item.description)}
                        </div>
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-xs text-gray-500">
                            {safeValue(item.filename)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {safeValue(item.size)}
                          </span>
                          <span className="text-xs text-gray-500">
                            Uploaded by {safeValue(item.uploadedBy?.name)} •{' '}
                            {item.uploadedAt
                              ? new Date(item.uploadedAt).toLocaleString()
                              : 'UnKnown'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <span className="text-sm text-white">UnKnown</span>
              )}
            </div>
          </div>
          */}
          {/* (Optional) Timeline could be added here */}
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Asset Information */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">
              Asset Information
            </h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-400">Asset Name</div>
                <div className="text-sm font-medium text-white mt-1">
                  {safeValue(
                    finding.assetName
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Asset Type</div>
                <div className="text-sm font-medium text-white mt-1">
                  {safeValue(
                    finding.assetTypeName
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">
              Quick Actions
            </h2>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => UpdateFinding(finding, Status111Enum.FalsePositive)}
                disabled
              >
                <X className="h-4 w-4 mr-2" />
                Mark as False Positive
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => UpdateFinding(finding, Status111Enum.Duplicate)}
                disabled
              >
                <Copy className="h-4 w-4 mr-2" />
                Mark as Duplicate
              </Button>

            </div>
          </div>

          {/* Timeline */}
          <Timeline entries={logEntries || []} />

        </div>
      </div>
    </div>
  );
};

export default FindingDetail;
